import React, { useState, useEffect } from 'react';
import { SupervisedIcons } from './StudentIcons';
import { EnuSourceItem } from '../types';
import { supervisedStudentApi } from '@/features/workspace/api';

interface EnuSourcesOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EnuSourcesOverlay: React.FC<EnuSourcesOverlayProps> = ({
  isOpen,
  onClose,
}) => {
  const [sources, setSources] = useState<EnuSourceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    loadSources();
  }, [isOpen]);

  const loadSources = async () => {
    setLoading(true);
    try {
      const data = await supervisedStudentApi.enuSources();
      setSources(data || []);
    } catch {
      setSources([
        {
          id: 'enu-1',
          title: 'Научная библиотека ЕНУ им. Л.Н. Гумилева (e-Library)',
          category: 'Электронная библиотека',
          url: 'https://lib.enu.kz',
          description: 'Полнотекстовый доступ к фондам университетской библиотеки, диссертациям, авторефератам и подписанным мировым базам данных.',
        },
        {
          id: 'enu-2',
          title: 'Научный журнал «Вестник ЕНУ» (Серии филологическая, техническая, экономическая)',
          category: 'Научные журналы',
          url: 'https://bulphil.enu.kz',
          description: 'Официальное рецензируемое периодическое издание, входящее в перечень КОКСНВО МНВО РК и индексируемое в РИНЦ.',
        },
        {
          id: 'enu-3',
          title: 'Положение о научно-исследовательской работе студентов (НИРС ЕНУ)',
          category: 'Регламенты и стандарты',
          url: 'https://enu.kz/ru/science/nirs',
          description: 'Нормативный регламент организации исследовательской деятельности обучающихся бакалавриата и магистратуры, порядок финансирования и отчетности.',
        },
        {
          id: 'enu-4',
          title: 'Кодекс академической честности и противодействия плагиату ЕНУ',
          category: 'Регламенты и стандарты',
          url: 'https://enu.kz/ru/about/integrity',
          description: 'Требования к оригинальности рукописей, этике цитирования и порядок проверки работ в системе Antiplagiat.',
        },
        {
          id: 'enu-5',
          title: 'Репозиторий диссертационных советов ЕНУ',
          category: 'Диссертационные советы',
          url: 'https://enu.kz/ru/science/dissertacionnye-sovety',
          description: 'Архив докторских диссертаций, заключений экспертных комиссий и графики защит PhD.',
        },
        {
          id: 'enu-6',
          title: 'НИИ Тюркологии и алтаистики ЕНУ',
          category: 'Научные подразделения',
          url: 'https://enu.kz/ru/science/nii-turkology',
          description: 'Профильный научно-исследовательский институт, ведущий фундаментальные лингвистические и исторические исследования.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (url: string, id: string) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const categories = [
    { key: 'ALL', label: 'Все источники' },
    { key: 'Регламенты и стандарты', label: 'Регламенты и ГОСТы' },
    { key: 'Электронная библиотека', label: 'Библиотека и репозитории' },
    { key: 'Научные журналы', label: 'Вестники ЕНУ' },
    { key: 'Диссертационные советы', label: 'Диссертационные советы' },
    { key: 'Научные подразделения', label: 'НИИ и лаборатории' },
  ];

  const filtered = sources.filter((s) => selectedCategory === 'ALL' || s.category === selectedCategory);

  if (!isOpen) return null;

  return (
    <div className="cab-overlay">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold flex items-center gap-1.5 transition border border-slate-700"
            >
              &larr; Назад
            </button>
            <div className="p-2.5 rounded-xl bg-blue-500/20 text-blue-400">
              <SupervisedIcons.Enu className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-100">Официальные источники ENU</h2>
              <p className="text-xs text-slate-400">Верифицированный каталог информационных ресурсов и регламентов университета</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="cab-btn icon"
          >
            <svg width={20} height={20} className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-5 border-b border-slate-800">
          {categories.map((c) => (
            <button
              key={c.key}
              onClick={() => setSelectedCategory(c.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition ${
                selectedCategory === c.key
                  ? 'bg-blue-600 text-white shadow'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-20 text-center text-sm text-slate-400">Загрузка каталога источников...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filtered.map((s) => (
                <div
                  key={s.id}
                  className="p-5 rounded-xl bg-slate-800/40 border border-slate-700/50 hover:border-blue-500/40 transition flex flex-col justify-between"
                >
                  <div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-700 text-blue-300">
                      {s.category}
                    </span>
                    <h3 className="text-sm font-bold text-slate-100 mt-2 hover:text-blue-300 transition">
                      {s.title}
                    </h3>
                    <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{s.description}</p>
                    <div className="text-[11px] font-mono text-blue-400/80 mt-2 truncate">
                      {s.url}
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-700/40 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => handleCopy(s.url, s.id)}
                      className="text-xs text-slate-400 hover:text-slate-200 transition"
                    >
                      {copiedId === s.id ? 'Ссылка скопирована' : 'Копировать адрес'}
                    </button>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold transition flex items-center gap-1"
                    >
                      Перейти на портал &rarr;
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
